import { Calendar } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { Task } from "../mock/types";
import {
	allAssignees,
	formatDueDate,
	projectsById,
	sprintsById,
	stagesByKey,
	typesById,
	STATUS_VARIANT,
} from "../mock/helpers";
import { AvatarStack } from "./AvatarStack";
import { PriorityBadge, TypeChip } from "./bits";

export function TaskTable({
	tasks,
	onOpenTask,
}: {
	tasks: Task[];
	onOpenTask: (task: Task) => void;
}) {
	if (tasks.length === 0) {
		return (
			<div className="rounded-xl border border-border bg-surface py-16 text-center text-sm text-muted">
				No tasks match your filters.
			</div>
		);
	}

	return (
		<div className="rounded-xl border border-border bg-surface overflow-x-auto">
			<table className="w-full text-sm">
				<thead>
					<tr className="border-b border-border text-left text-xs text-muted">
						<th className="font-medium px-4 py-3">Type</th>
						<th className="font-medium px-4 py-3">Task</th>
						<th className="font-medium px-4 py-3">Project</th>
						<th className="font-medium px-4 py-3">Priority</th>
						<th className="font-medium px-4 py-3">Status</th>
						<th className="font-medium px-4 py-3">Assignee</th>
						<th className="font-medium px-4 py-3">Sprint</th>
						<th className="font-medium px-4 py-3">Due</th>
					</tr>
				</thead>
				<tbody>
					{tasks.map((task) => {
						const type = task.task_type_id
							? typesById.get(task.task_type_id)
							: null;
						const stage = stagesByKey.get(task.status);
						const sprint = task.sprint_id
							? sprintsById.get(task.sprint_id)
							: null;
						return (
							<tr
								key={task.id}
								onClick={() => onOpenTask(task)}
								className="border-b border-border last:border-0 hover:bg-muted-subtle cursor-pointer transition-colors"
							>
								<td className="px-4 py-3">
									{type ? <TypeChip type={type} /> : "—"}
								</td>
								<td className="px-4 py-3 max-w-[320px]">
									<div className="flex items-center gap-2">
										{task.ticket_code && (
											<span className="text-[11px] text-muted font-mono shrink-0">
												#{task.ticket_code}
											</span>
										)}
										<span className="font-medium text-foreground truncate">
											{task.title}
										</span>
									</div>
								</td>
								<td className="px-4 py-3 text-muted">
									{projectsById.get(task.project_id)?.name ??
										"—"}
								</td>
								<td className="px-4 py-3">
									<PriorityBadge priority={task.priority} />
								</td>
								<td className="px-4 py-3">
									<Badge variant={STATUS_VARIANT[task.status]}>
										{stage?.name ?? task.status}
									</Badge>
								</td>
								<td className="px-4 py-3">
									{allAssignees(task).length > 0 ? (
										<AvatarStack
											profiles={allAssignees(task)}
											size="sm"
										/>
									) : (
										<span className="text-xs text-muted">
											Unassigned
										</span>
									)}
								</td>
								<td className="px-4 py-3 text-muted text-xs">
									{sprint?.name ?? "—"}
								</td>
								<td className="px-4 py-3 text-muted text-xs">
									{task.due_date ? (
										<span className="flex items-center gap-1">
											<Calendar className="h-3 w-3" />
											{formatDueDate(task.due_date)}
										</span>
									) : (
										"—"
									)}
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}
